import api from './api';

/**
 * Service for automated generation of all project tasks
 */
const automatedGenerationService = {
  /**
   * Start automated generation for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Generation result
   */
  startGeneration: async (projectId) => {
    try {
      console.log(`Starting automated generation for project: ${projectId}`);
      const response = await api.post(`/automated-generation/projects/${projectId}/start`, {});
      return response.data;
    } catch (error) {
      console.error("Error starting automated generation:", error);
      throw error.response?.data?.detail || error.message || 'Failed to start automated generation';
    }
  },

  /**
   * Start automated generation with streaming progress updates
   * @param {string} projectId - Project ID
   * @param {Function} onProgress - Progress callback function
   * @param {Function} onComplete - Completion callback function
   * @param {Function} onError - Error callback function
   * @returns {Promise<void>}
   */
  startGenerationWithStreaming: async (projectId, onProgress, onComplete, onError) => {
    console.log(`Starting automated generation with streaming for project: ${projectId}`);
    
    try {
      // Ensure we have a valid token
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        throw new Error('Authentication token missing');
      }
      
      // Create URL for streaming endpoint
      const url = new URL(`${api.defaults.baseURL}/automated-generation/projects/${projectId}/start-stream`);
      
      // Make the streaming request
      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({}),
        mode: 'cors'
      });
      
      // Handle non-success responses
      if (!response.ok) {
        let errorMessage;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || `Server returned ${response.status}: ${response.statusText}`;
        } catch (jsonError) {
          errorMessage = `Server returned ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
      
      // Ensure browser supports ReadableStream
      if (!response.body) {
        throw new Error('ReadableStream not supported in this browser.');
      }
      
      // Get the reader from the response body stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      
      console.log('Automated generation stream connected, awaiting data...');
      
      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            console.log('Automated generation stream complete');
            break;
          }
          
          // Decode the chunk
          const rawChunk = decoder.decode(value, { stream: true });
          
          // Parse SSE data format - extract content after "data: " prefix
          const lines = rawChunk.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const eventData = line.substring(6); // Remove "data: " prefix
              
              // Skip empty lines and done signals
              if (!eventData.trim() || eventData.trim() === '[DONE]') {
                continue;
              }
              
              try {
                const parsedData = JSON.parse(eventData);
                
                // Handle different event types
                if (parsedData.status === 'error') {
                  onError && onError(new Error(parsedData.error || 'Generation failed'));
                  return;
                } else if (parsedData.status === 'completed') {
                  onComplete && onComplete(parsedData.result);
                  return;
                } else {
                  // Progress update
                  onProgress && onProgress(parsedData);
                }
              } catch (parseError) {
                console.warn('Error parsing SSE event:', parseError, 'Raw data:', eventData);
              }
            }
          }
        }
      } catch (streamError) {
        console.error('Error reading stream:', streamError);
        onError && onError(streamError);
      }
      
    } catch (error) {
      console.error("Error in automated generation streaming:", error);
      onError && onError(error);
    }
  },

  /**
   * Get current generation status for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Generation status
   */
  getGenerationStatus: async (projectId) => {
    try {
      console.log(`Getting generation status for project: ${projectId}`);
      const response = await api.get(`/automated-generation/projects/${projectId}/status`);
      return response.data;
    } catch (error) {
      console.error("Error getting generation status:", error);
      throw error.response?.data?.detail || error.message || 'Failed to get generation status';
    }
  },

  /**
   * Pause ongoing generation for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Pause result
   */
  pauseGeneration: async (projectId) => {
    try {
      console.log(`Pausing generation for project: ${projectId}`);
      const response = await api.post(`/automated-generation/projects/${projectId}/pause`, {});
      return response.data;
    } catch (error) {
      console.error("Error pausing generation:", error);
      throw error.response?.data?.detail || error.message || 'Failed to pause generation';
    }
  },

  /**
   * Resume paused generation for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Resume result
   */
  resumeGeneration: async (projectId) => {
    try {
      console.log(`Resuming generation for project: ${projectId}`);
      const response = await api.post(`/automated-generation/projects/${projectId}/resume`, {});
      return response.data;
    } catch (error) {
      console.error("Error resuming generation:", error);
      throw error.response?.data?.detail || error.message || 'Failed to resume generation';
    }
  },

  /**
   * Get task dependencies and execution order
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Dependencies information
   */
  getTaskDependencies: async (projectId) => {
    try {
      console.log(`Getting task dependencies for project: ${projectId}`);
      const response = await api.get(`/automated-generation/projects/${projectId}/dependencies`);
      return response.data;
    } catch (error) {
      console.error("Error getting task dependencies:", error);
      throw error.response?.data?.detail || error.message || 'Failed to get task dependencies';
    }
  },

  /**
   * Reset generation state for a project (for debugging/recovery)
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Reset result
   */
  resetGeneration: async (projectId) => {
    try {
      console.log(`Resetting generation state for project: ${projectId}`);
      const response = await api.delete(`/automated-generation/projects/${projectId}/reset`);
      return response.data;
    } catch (error) {
      console.error("Error resetting generation:", error);
      throw error.response?.data?.detail || error.message || 'Failed to reset generation';
    }
  },

  /**
   * Restart generation from a specific task or from the beginning
   * @param {string} projectId - Project ID
   * @param {string} fromTask - Task to restart from (optional)
   * @returns {Promise<Object>} Restart result
   */
  restartGeneration: async (projectId, fromTask = null) => {
    try {
      console.log(`Restarting generation for project: ${projectId}${fromTask ? ` from task: ${fromTask}` : ''}`);
      const url = `/automated-generation/projects/${projectId}/restart${fromTask ? `?from_task=${fromTask}` : ''}`;
      const response = await api.post(url, {});
      return response.data;
    } catch (error) {
      console.error("Error restarting generation:", error);
      throw error.response?.data?.detail || error.message || 'Failed to restart generation';
    }
  },

  /**
   * Restart generation with streaming progress updates
   * @param {string} projectId - Project ID
   * @param {string} fromTask - Task to restart from (optional)
   * @param {Function} onProgress - Progress callback function
   * @param {Function} onComplete - Completion callback function
   * @param {Function} onError - Error callback function
   * @returns {Promise<void>}
   */
  restartGenerationWithStreaming: async (projectId, fromTask, onProgress, onComplete, onError) => {
    console.log(`Restarting generation with streaming for project: ${projectId}${fromTask ? ` from task: ${fromTask}` : ''}`);
    
    try {
      // Ensure we have a valid token
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        throw new Error('Authentication token missing');
      }
      
      // Create URL for streaming restart endpoint
      const url = new URL(`${api.defaults.baseURL}/automated-generation/projects/${projectId}/restart-stream`);
      if (fromTask) {
        url.searchParams.set('from_task', fromTask);
      }
      
      // Make the streaming request
      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({}),
        mode: 'cors'
      });
      
      // Handle non-success responses
      if (!response.ok) {
        let errorMessage;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || `Server returned ${response.status}: ${response.statusText}`;
        } catch (jsonError) {
          errorMessage = `Server returned ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
      
      // Ensure browser supports ReadableStream
      if (!response.body) {
        throw new Error('ReadableStream not supported in this browser.');
      }
      
      // Get the reader from the response body stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      
      console.log('Restart generation stream connected, awaiting data...');
      
      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            console.log('Restart generation stream complete');
            break;
          }
          
          // Decode the chunk
          const rawChunk = decoder.decode(value, { stream: true });
          
          // Parse SSE data format - extract content after "data: " prefix
          const lines = rawChunk.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const eventData = line.substring(6); // Remove "data: " prefix
              
              // Skip empty lines and done signals
              if (!eventData.trim() || eventData.trim() === '[DONE]') {
                continue;
              }
              
              try {
                const parsedData = JSON.parse(eventData);
                
                // Handle different event types
                if (parsedData.status === 'error') {
                  onError && onError(new Error(parsedData.error || 'Restart failed'));
                  return;
                } else if (parsedData.status === 'completed') {
                  onComplete && onComplete(parsedData.result);
                  return;
                } else {
                  // Progress update
                  onProgress && onProgress(parsedData);
                }
              } catch (parseError) {
                console.warn('Error parsing SSE event:', parseError, 'Raw data:', eventData);
              }
            }
          }
        }
      } catch (streamError) {
        console.error('Error reading restart stream:', streamError);
        onError && onError(streamError);
      }
      
    } catch (error) {
      console.error("Error in restart generation streaming:", error);
      onError && onError(error);
    }
  },

  /**
   * Get automated generation results for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Generation results
   */
  getGenerationResults: async (projectId) => {
    try {
      console.log(`Getting generation results for project: ${projectId}`);
      const response = await api.get(`/automated-generation/projects/${projectId}/results`);
      return response.data;
    } catch (error) {
      console.error("Error getting generation results:", error);
      throw error.response?.data?.detail || error.message || 'Failed to get generation results';
    }
  },

  /**
   * Get automated generation result for a specific task
   * @param {string} projectId - Project ID
   * @param {string} taskId - Task ID
   * @returns {Promise<Object>} Task generation result
   */
  getTaskGenerationResult: async (projectId, taskId) => {
    try {
      console.log(`Getting generation result for task: ${taskId} in project: ${projectId}`);
      const response = await api.get(`/automated-generation/projects/${projectId}/results/${taskId}`);
      return response.data;
    } catch (error) {
      console.error("Error getting task generation result:", error);
      throw error.response?.data?.detail || error.message || 'Failed to get task generation result';
    }
  },

  /**
   * Check foundation content status for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Foundation content status
   */
  getFoundationContentStatus: async (projectId) => {
    try {
      console.log(`Checking foundation content status for project: ${projectId}`);
      const response = await api.get(`/automated-generation/projects/${projectId}/foundation-status`);
      return response.data;
    } catch (error) {
      console.error("Error checking foundation content status:", error);
      throw error.response?.data?.detail || error.message || 'Failed to check foundation content status';
    }
  },

  /**
   * Generate AI recommendations based on project analysis
   * @param {string} projectId - Project ID
   * @param {Object} analysisData - Analysis data from MCP
   * @returns {Promise<Object>} AI recommendations
   */
  generateAiRecommendations: async (projectId, analysisData) => {
    try {
      console.log(`Generating AI recommendations for project: ${projectId}`);
      
      const response = await api.post(`/automated-generation/ai-recommendations`, {
        project_id: projectId,
        analysis_data: analysisData,
        recommendation_types: [
          'code_improvements',
          'architecture_suggestions',
          'performance_optimizations',
          'security_enhancements',
          'best_practices'
        ]
      });

      return response.data;
    } catch (error) {
      console.error('Error generating AI recommendations:', error);
      throw error.response?.data?.detail || error.message || 'Failed to generate AI recommendations';
    }
  },

  /**
   * Apply AI recommendations to project
   * @param {string} projectId - Project ID
   * @param {Array} recommendations - Selected recommendations to apply
   * @returns {Promise<Object>} Application results
   */
  applyAiRecommendations: async (projectId, recommendations) => {
    try {
      console.log(`Applying AI recommendations for project: ${projectId}`);
      
      const response = await api.post(`/automated-generation/apply-recommendations`, {
        project_id: projectId,
        recommendations: recommendations,
        auto_commit: true
      });

      return response.data;
    } catch (error) {
      console.error('Error applying AI recommendations:', error);
      throw error.response?.data?.detail || error.message || 'Failed to apply AI recommendations';
    }
  },

  /**
   * Get AI recommendations history
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Recommendations history
   */
  getRecommendationsHistory: async (projectId) => {
    try {
      console.log(`Getting AI recommendations history for project: ${projectId}`);
      
      const response = await api.get(`/automated-generation/recommendations/${projectId}/history`);
      return response.data;
    } catch (error) {
      console.error('Error getting recommendations history:', error);
      throw error.response?.data?.detail || error.message || 'Failed to get recommendations history';
    }
  },

  /**
   * Start intelligent code analysis and recommendations
   * @param {string} projectId - Project ID
   * @param {Object} options - Analysis options
   * @returns {Promise<Object>} Analysis results with recommendations
   */
  startIntelligentAnalysis: async (projectId, options = {}) => {
    try {
      console.log(`Starting intelligent analysis for project: ${projectId}`);
      
      const response = await api.post(`/automated-generation/intelligent-analysis`, {
        project_id: projectId,
        analysis_depth: options.depth || 'comprehensive',
        focus_areas: options.focusAreas || ['performance', 'security', 'maintainability'],
        include_suggestions: true,
        generate_fixes: options.generateFixes || false
      });

      return response.data;
    } catch (error) {
      console.error('Error starting intelligent analysis:', error);
      throw error.response?.data?.detail || error.message || 'Failed to start intelligent analysis';
    }
  },

  /**
   * Get predictive insights for project development
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Predictive insights
   */
  getPredictiveInsights: async (projectId) => {
    try {
      console.log(`Getting predictive insights for project: ${projectId}`);
      
      const response = await api.get(`/automated-generation/predictive-insights/${projectId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting predictive insights:', error);
      throw error.response?.data?.detail || error.message || 'Failed to get predictive insights';
    }
  }
};

export default automatedGenerationService;